import { z } from 'zod';
import { PLATEFORMES, TYPES_CONTRAT, type Plateforme } from './annonces.js';
import type { Source } from './candidature.js';
import type { LienPlateforme } from './lien-plateforme.js';

/**
 * États d'un message dans `emails_ingestion`. `a_rattacher` est la file du
 * responsable RH : le message est compris, mais aucune annonce ne lui
 * correspond (docs/INGESTION_EMAIL.md § Rattachement).
 */
export const STATUTS_INGESTION = [
  'a_traiter',
  'traite',
  'a_rattacher',
  'ignore',
  'erreur',
] as const;
export const statutIngestionSchema = z.enum(STATUTS_INGESTION);
export type StatutIngestion = z.infer<typeof statutIngestionSchema>;

export const LIBELLES_STATUT_INGESTION: Record<StatutIngestion, string> = {
  a_traiter: 'À traiter',
  traite: 'Traité',
  a_rattacher: 'À rattacher',
  ignore: 'Ignoré',
  erreur: 'En erreur',
};

/** Expéditeurs reconnus (docs/INGESTION_EMAIL.md § Sources reconnues). */
export const EXPEDITEURS: ReadonlyArray<{ source: Source; motif: RegExp }> = [
  { source: 'tanitjobs', motif: /(^|@|\.)tanitjobs\.com$/i },
  { source: 'tunisietravail', motif: /(^|@|\.)tunisietravail\.net$/i },
  { source: 'keejob', motif: /(^|@|\.)keejob(mail)?\.com$/i },
];

/**
 * Ce qu'un parseur tire d'un message, avant tout accès à la base.
 * Le contenu reste **de la donnée** : rien ici ne déclenche d'action.
 */
export interface DonneesCandidature {
  source: Source;
  nom: string | null;
  prenom: string | null;
  email: string | null;
  telephone: string | null;
  ville: string | null;
  /** Référence interne `PG-...` trouvée dans le corps. */
  reference: string | null;
  /** Numéro d'offre de la plateforme, ex. `133205`. */
  offreExterne: string | null;
  /** Intitulé tel que publié, dernier recours de rattachement. */
  intitulePublie: string | null;
  /** Liens externes (Google Drive…) : signalés, jamais téléchargés. */
  liensExternes: string[];
  /** Tunisie Travail : lien vers la liste des CV de l'offre ou la page de l'offre (lien-plateforme.ts). */
  lienPlateforme?: LienPlateforme | null;
  /** Texte du message, conservé pour l'extraction et l'affichage. */
  texte: string;
}

export interface MessageIngestion {
  id: string;
  messageId: string;
  expediteur: string | null;
  objet: string | null;
  recuLe: string | null;
  source: string | null;
  statut: StatutIngestion;
  erreur: string | null;
  tentatives: number;
  candidatureId: string | null;
  donnees: DonneesCandidature | null;
}

export const rattachementSchema = z.object({
  annonceId: z.string().uuid(),
});

/** Motifs de rattachement, du plus sûr au plus faible. */
export const MOYENS_RATTACHEMENT = [
  'reference',
  'offre_externe',
  'intitule',
  'annonce_detectee',
  'manuel',
] as const;
export type MoyenRattachement = (typeof MOYENS_RATTACHEMENT)[number];

export const LIBELLES_MOYEN: Record<MoyenRattachement, string> = {
  reference: 'Référence interne',
  offre_externe: "Numéro d'offre plateforme",
  intitule: 'Intitulé publié',
  annonce_detectee: 'Annonce détectée',
  manuel: 'Rattaché à la main',
};

/**
 * Mention de l'enseigne dans le corps d'une notification : avec une référence
 * `PG-...` bien formée, elle suffit à reconnaître une annonce de la maison
 * publiée hors de l'application (docs/INGESTION_EMAIL.md § Annonce détectée).
 * Deux marqueurs sont exigés : une référence seule peut se lire ailleurs.
 */
const MOTIF_ENSEIGNE = /\bplaisirs\s+gourmands\b/i;

export function porteMentionEnseigne(...textes: Array<string | null | undefined>): boolean {
  return textes.some((texte) => (texte ? MOTIF_ENSEIGNE.test(sansAccentsSimple(texte)) : false));
}

function sansAccentsSimple(valeur: string): string {
  return valeur.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Files BullMQ, partagées par l'API (qui dépose) et le worker (qui consomme).
 * `commandes` porte les demandes ponctuelles du responsable RH, comme la
 * relance d'un cycle de lecture IMAP (docs/API.md § /ingestion/relancer).
 */
export const FILE_INGESTION = 'ingestion';
export const FILE_EXTRACTION = 'extraction';
export const FILE_COMMANDES = 'commandes';

export interface TravailIngestion {
  emailId: string;
  /** Rattachement imposé par le responsable RH depuis l'écran d'administration. */
  annonceId?: string;
}

export interface TravailExtraction {
  candidatureId: string;
}

export const COMMANDE_RELANCE = 'relancer-imap';

/** Trois tentatives espacées, puis le message reste en erreur (§ Erreurs). */
export const TENTATIVES_MAX = 3;


/**
 * Annonce pressentie derrière des messages que rien ne rattache
 * (docs/INGESTION_EMAIL.md § Annonce détectée). Les messages « à rattacher »
 * sont regroupés par offre : même plateforme, même numéro d'offre, même
 * intitulé publié. Le responsable RH crée l'annonce d'un geste, et tous les
 * messages du groupe sont rejoués derrière elle.
 */
export interface AnnonceDetectee {
  /** Signature du groupe : `source|offre|intitulé normalisé`. */
  cle: string;
  source: Source;
  /** Plateforme à déclarer sur l'annonce ; un transfert compte comme `direct`. */
  plateforme: Plateforme;
  offreExterne: string | null;
  intitulePublie: string | null;
  /** Référence `PG-...` lue dans les messages mais inconnue en base, s'il y en a une. */
  reference: string | null;
  messages: number;
  premierRecuLe: string | null;
  dernierRecuLe: string | null;
  /** Quelques candidats, pour reconnaître l'offre d'un coup d'œil. */
  candidats: string[];
  messageIds: string[];
}

/** Création d'une annonce depuis un groupe de messages non rattachés. */
export const creationAnnonceDetecteeSchema = z.object({
  familleId: z.string().uuid(),
  intitule: z.string().trim().min(3, 'Intitulé : 3 caractères au moins').max(200),
  typeContrat: z.enum(TYPES_CONTRAT).nullable().default(null),
  plateforme: z.enum(PLATEFORMES),
  offreExterne: z
    .string()
    .trim()
    .max(40)
    .regex(/^[\w.-]*$/, 'Numéro d’offre : chiffres et lettres')
    .nullable()
    .default(null),
  intitulePublie: z.string().trim().max(200).nullable().default(null),
  messageIds: z.array(z.string().uuid()).min(1).max(200),
});
export type CreationAnnonceDetectee = z.infer<typeof creationAnnonceDetecteeSchema>;

/**
 * Rattachement d'un groupe entier à une annonce qui existe déjà : l'offre
 * était une annonce connue sous un autre intitulé, ou republiée. Le geste est
 * le même que le rattachement message par message, en une fois.
 */
export const rattachementGroupeSchema = z
  .object({
    annonceId: z.string().uuid(),
    messageIds: z.array(z.string().uuid()).min(1).max(200),
  })
  .strict();
export type RattachementGroupe = z.infer<typeof rattachementGroupeSchema>;

export interface AnnonceCreeeDepuisMessages {
  annonceId: string;
  reference: string;
  /** Messages remis en file derrière la nouvelle annonce. */
  messages: number;
}
